import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { OFFERS_DISCLOSURE, type AttemptClaimResponse, type AttemptView, type OfferKind } from "@townsy/contracts";
import { fetchAttempt, postCancelAttempt, postClaimAttempt, postRefreshAttemptLink, postStartAttempt } from "../../api/rewards";
import { invalidateAfterAttempt } from "../../api/rewardsQueries";
import { makeIdempotencyKey } from "../../homes/commands";
import { formatCountdown, msUntil, serverNow } from "../../lib/serverClock";
import { formatCoins } from "../../lib/wallet";
import { AttemptController, INITIAL_ATTEMPT_FLOW, flowPhase, type AttemptFlowState, type AttemptPhase, type ClaimInfo } from "../../rewards/attemptMachine";
import { OFFER_KIND_LABEL, eligibilityCopy, kindHowTo, rewardText } from "../../rewards/copy";
import { useTownStore, type TaskRequest } from "../../state/store";
import { useServerNow } from "../checkout/useServerNow";
import { Dialog } from "../Dialog";

/**
 * One sponsored task, start to finish (docs/07 section 2, ADR-0011 #3): website visit and return,
 * on-Townsy quiz, or partner-confirmed outcome. A modal dialog (movement stops while it is open, A09),
 * the same in the 3D town and on plain pages. The sponsorship disclosure and the honest verification
 * label are always visible; every number comes from the server's attempt; coins are announced only
 * from the server's claim receipt.
 */

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** The page's one task dialog (mounted by the town layout and by the plain page shell). */
export function TaskHost() {
  const task = useTownStore((s) => s.task);
  const close = useTownStore((s) => s.closeTask);
  return task ? <AttemptDialog key={task.id} request={task} onClose={close} /> : null;
}

/**
 * Toast copy for a claim receipt (the balance is the server's number after the award). A partner task the server
 * paid right after the sponsor's callback, while this page watched it, is announced as new (its receipt says
 * `replayed` because the server's claim came first); a receipt for a task that was already paid says so.
 */
export function claimToastText(res: Pick<AttemptClaimResponse, "claim" | "replayed"> & { attempt: Pick<AttemptView, "sponsorLabel"> }, info?: ClaimInfo): string {
  if (res.replayed && info?.bySponsorWhileWatching) {
    return `${rewardText(res.claim.rewardCoins)} from ${res.attempt.sponsorLabel}: they confirmed your task. Your coin balance: ${formatCoins(res.claim.coinsAvailableAfter)}.`;
  }
  if (res.replayed) return `This reward was already added. Your coin balance: ${formatCoins(res.claim.coinsAvailableAfter)}.`;
  return `${rewardText(res.claim.rewardCoins)} from ${res.attempt.sponsorLabel}. Your coin balance: ${formatCoins(res.claim.coinsAvailableAfter)}.`;
}

function AttemptDialog({ request, onClose }: { request: TaskRequest; onClose(): void }) {
  const qc = useQueryClient();
  const pushToast = useTownStore((s) => s.pushToast);
  const [state, setState] = useState<AttemptFlowState>(() => ({ ...INITIAL_ATTEMPT_FLOW, offer: request.offer, busy: request.attemptId || request.offer?.activeAttemptId ? "loading" : null }));
  const ctrlRef = useRef<AttemptController | null>(null);
  const propertyId = request.offer?.propertyId ?? null;

  useEffect(() => {
    const ctrl = new AttemptController(
      {
        api: {
          start: postStartAttempt,
          get: (id) => fetchAttempt(id),
          refreshLink: postRefreshAttemptLink,
          claim: postClaimAttempt,
          cancel: postCancelAttempt,
        },
        now: serverNow,
        sleep,
        makeKey: (prefix) => makeIdempotencyKey(prefix),
        onChange: setState,
        onClaimed: (res, info) => {
          invalidateAfterAttempt(qc, res.attempt.propertyId);
          pushToast(claimToastText(res, info), "success");
        },
        onAttemptChanged: (a) => invalidateAfterAttempt(qc, a.propertyId ?? propertyId),
      },
      request.offer ? { offer: request.offer, attemptId: request.attemptId } : { attemptId: request.attemptId ?? "" },
    );
    ctrlRef.current = ctrl;
    void ctrl.begin();
    return () => {
      ctrl.dispose();
      ctrlRef.current = null;
    };
  }, [request, qc, pushToast, propertyId]);

  const now = useServerNow(true);
  const phase = flowPhase(state, now);
  const a = state.attempt;

  // The reservation ends: ask the server (it may have been claimed at the last second; never assume).
  const expiresAt = a && a.state === "active" ? a.expiresAt : null;
  useEffect(() => {
    if (!expiresAt) return;
    const t = setTimeout(() => void ctrlRef.current?.expiryReached(), msUntil(expiresAt) + 300);
    return () => clearTimeout(t);
  }, [expiresAt]);

  const title = state.offer?.title ?? a?.title ?? "Sponsored task";
  const active = Boolean(a && a.state === "active" && phase !== "expired");
  const busy = state.busy !== null;
  const handlers = attemptHandlers(
    () => ctrlRef.current,
    () => request.attemptId ?? request.offer?.activeAttemptId ?? null,
  );

  return (
    <Dialog
      title={title}
      onClose={busy && state.busy !== "loading" ? () => undefined : onClose}
      describedBy="task-body"
      closeLabel={active ? "Close (your task keeps running)" : "Close task"}
      footer={
        <>
          {active ? (
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => void ctrlRef.current?.cancel()} data-testid="task-cancel">
              {state.busy === "canceling" ? "Canceling…" : "Cancel task"}
            </button>
          ) : null}
          <button type="button" className={phase === "claimed" ? "btn btn-primary" : "btn"} onClick={onClose} disabled={busy && state.busy !== "loading"} data-testid="task-close">
            {phase === "claimed" ? "Done" : active ? "Close (keep the task)" : "Close"}
          </button>
        </>
      }
    >
      <div id="task-body" className="stack task-dialog" data-testid="task-dialog" data-phase={phase} data-kind={a?.kind ?? state.offer?.kind ?? ""}>
        <AttemptBody state={state} phase={phase} now={now} handlers={handlers} />
      </div>
    </Dialog>
  );
}

export interface AttemptHandlers {
  start(): void;
  restart(): void;
  linkOpened(): void;
  refreshLink(): void;
  claim(answerIndex?: number): void;
  showAttempt(attemptId: string): void;
  retryLoad(): void;
}

type AttemptActions = Pick<AttemptController, "start" | "restart" | "linkOpened" | "refreshLink" | "claim" | "switchTo" | "load">;

/**
 * The dialog's buttons. Each reads the controller when it is pressed, never when the dialog rendered: the controller
 * is created in an effect after the first render, and a task not yet started changes no state that would render the
 * dialog again, so a handler bound at render time could stay bound to nothing (staging 2026-10-22: "Start task"
 * pressed right after the dialog opened did nothing).
 */
export function attemptHandlers(controller: () => AttemptActions | null, resumeId: () => string | null): AttemptHandlers {
  return {
    start: () => void controller()?.start(),
    restart: () => void controller()?.restart(),
    linkOpened: () => controller()?.linkOpened(),
    refreshLink: () => void controller()?.refreshLink(),
    claim: (answer) => void controller()?.claim(answer),
    showAttempt: (id) => void controller()?.switchTo(id),
    retryLoad: () => {
      const id = resumeId();
      if (id) void controller()?.load(id);
    },
  };
}

/** Everything inside the dialog for one phase (pure props; server-rendered in unit tests). */
export function AttemptBody({ state, phase, now, handlers }: { state: AttemptFlowState; phase: AttemptPhase; now: number; handlers: AttemptHandlers }) {
  const a = state.attempt;
  const o = state.offer;
  const kind: OfferKind | null = a?.kind ?? o?.kind ?? null;
  const reward = a?.rewardCoins ?? o?.rewardCoins ?? null;
  const sponsor = a?.sponsorLabel ?? o?.sponsorLabel ?? "the sponsor";
  const host = a?.destinationHost ?? o?.destinationHost ?? null;
  const verification = a?.verificationLabel ?? o?.verificationLabel ?? null;
  const busy = state.busy;
  const left = a ? msUntil(a.expiresAt, now) : 0;
  const showTimer = Boolean(a && a.state === "active" && left > 0 && phase !== "claimed");

  return (
    <>
      <div className="row task-badges" style={{ gap: 6 }}>
        {reward !== null ? <span className="badge badge-coin">{rewardText(reward)}</span> : null}
        {kind ? <span className="badge">{OFFER_KIND_LABEL[kind]}</span> : null}
        {o?.isHouse ? <span className="badge badge-house">Townsy</span> : <span className="badge badge-sponsored">Sponsored</span>}
        <span className="small muted">
          {o?.isHouse ? "A Townsy task" : `Sponsored by ${sponsor}`}
          {o?.address ? ` · ${o.address}` : ""}
        </span>
      </div>
      {showTimer ? (
        <p className="small task-timer" role="timer" aria-live="off" data-testid="task-expiry" style={{ margin: 0 }}>
          Reserved for you: <strong>{formatCountdown(left)}</strong> <span className="muted">(by the server's clock)</span>
        </p>
      ) : null}

      <PhaseBody state={state} phase={phase} now={now} handlers={handlers} sponsor={sponsor} host={host} kind={kind} reward={reward} />

      {state.failure ? <FailureNotice state={state} handlers={handlers} /> : null}
      {busy === "claiming" ? <Status text="Checking with Townsy…" /> : null}
      {busy === "refreshing" ? <Status text="Getting a fresh link…" /> : null}

      {verification ? (
        <p className="small muted" style={{ margin: 0 }} data-testid="task-verification">
          <strong>What Townsy checks:</strong> {verification}
        </p>
      ) : null}
      <p className="notice notice-info small" role="note" style={{ margin: 0 }} data-testid="sponsored-disclosure">
        {o?.disclosure || OFFERS_DISCLOSURE}
      </p>
    </>
  );
}

function Status({ text }: { text: string }) {
  return (
    <p className="small" role="status" style={{ margin: 0 }}>
      <span className="spinner" aria-hidden="true" /> {text}
    </p>
  );
}

function FailureNotice({ state, handlers }: { state: AttemptFlowState; handlers: AttemptHandlers }) {
  const f = state.failure!;
  const tone = f.action === "retry" || f.action === "answer_again" ? "notice-warning" : "notice-danger";
  return (
    <div className={`notice ${tone} small`} role="alert" data-testid="task-error" data-code={f.code} style={{ margin: 0 }}>
      {f.message}
      {f.action === "sign_in" || f.action === "verify_email" ? (
        <>
          {" "}
          <Link to="/account">{f.action === "sign_in" ? "Create an account or sign in" : "Open your Account page"}</Link>
        </>
      ) : null}
      {f.action === "show_active" && f.attemptId ? (
        <>
          {" "}
          <button type="button" className="btn btn-sm" onClick={() => handlers.showAttempt(f.attemptId!)} data-testid="task-show-active">
            Show my task in progress
          </button>
        </>
      ) : null}
    </div>
  );
}

/** The resident's link to the sponsor: a plain, directly clicked anchor (never window.open after an await). */
function SponsorLink({ url, host, label, onOpened }: { url: string; host: string | null; label: string; onOpened(): void }) {
  return (
    <a
      className="btn btn-primary"
      href={url}
      target="_blank"
      rel="noopener noreferrer sponsored"
      onClick={onOpened}
      onAuxClick={(e) => {
        if (e.button === 1) onOpened();
      }}
      title={host ? `Opens ${host} in a new tab` : "Opens the sponsor's website in a new tab"}
      data-testid="task-link"
    >
      {label} ↗
    </a>
  );
}

function PhaseBody({
  state,
  phase,
  now,
  handlers,
  sponsor,
  host,
  kind,
  reward,
}: {
  state: AttemptFlowState;
  phase: AttemptPhase;
  now: number;
  handlers: AttemptHandlers;
  sponsor: string;
  host: string | null;
  kind: OfferKind | null;
  reward: number | null;
}): ReactNode {
  const a = state.attempt;
  const o = state.offer;
  const coins = reward !== null ? formatCoins(reward) : "the coins";
  const linkLabel = host ? `Open ${host}` : "Open the sponsor's website";
  const link = state.outboundUrl ? (
    <SponsorLink url={state.outboundUrl} host={host} label={linkLabel} onOpened={handlers.linkOpened} />
  ) : (
    <button type="button" className="btn btn-primary" onClick={handlers.refreshLink} disabled={state.busy !== null} data-testid="task-get-link">
      Get the link
    </button>
  );

  switch (phase) {
    case "loading":
      return <Status text="Loading your task…" />;
    case "error":
      return (
        <div className="row">
          <button type="button" className="btn btn-sm" onClick={handlers.retryLoad}>
            Try again
          </button>
        </div>
      );
    case "intro": {
      const copy = o ? eligibilityCopy(o.eligibility, { activeHere: Boolean(o.activeAttemptId) }) : null;
      return (
        <div className="stack" style={{ gap: 8 }} data-testid="task-intro">
          {o ? <p style={{ margin: 0 }}>{o.instructions}</p> : null}
          {kind ? <p className="small" style={{ margin: 0 }}>{kindHowTo(kind, o?.minReturnSeconds)}</p> : null}
          {o?.explanation && kind === "quiz" ? <p className="small muted" style={{ margin: 0 }}>{o.explanation}</p> : null}
          {copy && !copy.canStart && copy.message ? (
            <p className="notice notice-warning small" style={{ margin: 0 }} data-testid="task-eligibility">
              {copy.message}
            </p>
          ) : null}
          <div className="row">
            {copy?.canStart ? (
              <button type="button" className="btn btn-primary" onClick={handlers.start} disabled={state.busy !== null} data-testid="task-start" data-autofocus>
                {copy.label}
              </button>
            ) : copy?.cta === "sign_in" || copy?.cta === "verify_email" ? (
              <Link className="btn btn-primary" to="/account" data-testid="task-sign-in">
                {copy.label}
              </Link>
            ) : null}
          </div>
          {copy?.canStart ? (
            <p className="hint" style={{ margin: 0 }}>
              Starting reserves your reward for a few minutes (never past midnight UTC). You can cancel at any time; one task can run at a time.
            </p>
          ) : null}
        </div>
      );
    }
    case "starting":
      return <Status text="Reserving your reward…" />;
    case "open_link":
      if (kind === "partner_callback") {
        return (
          <div className="stack" style={{ gap: 8 }} data-testid="task-partner-open">
            {o ? <p style={{ margin: 0 }}>{o.instructions}</p> : null}
            <ol className="small task-steps">
              <li>Open the sponsor's website from here and complete the step it asks for{a?.partner?.conversionType ? ` (${a.partner.conversionType.replace(/_/g, " ")})` : ""}.</li>
              <li>{sponsor}'s server confirms it to Townsy. That can take a few minutes.</li>
              <li>Then the coins are added (or you claim them here).</li>
            </ol>
            <div className="row">{link}</div>
          </div>
        );
      }
      return (
        <div className="stack" style={{ gap: 8 }} data-testid="task-visit-open">
          {o ? <p style={{ margin: 0 }}>{o.instructions}</p> : null}
          <ol className="small task-steps">
            <li>Open {host ?? "the sponsor's website"} in a new tab with the button below.</li>
            <li>Come back to this window after at least {o?.minReturnSeconds ?? 15} seconds.</li>
            <li>Claim {coins}.</li>
          </ol>
          <div className="row">{link}</div>
          <p className="hint" style={{ margin: 0 }}>
            Townsy only records that you opened the link from here. It cannot see the website or what you do there.
          </p>
        </div>
      );
    case "confirming_click":
      return (
        <div className="stack" style={{ gap: 8 }} data-testid="task-confirming-click">
          {state.clickNotSeen ? (
            <>
              <p className="notice notice-warning small" style={{ margin: 0 }}>
                Townsy hasn't recorded your visit yet. Open the link from this window: a pop-up blocker or opening it in another way can stop it.
              </p>
              <div className="row">
                {link}
                <button type="button" className="btn btn-sm" onClick={handlers.refreshLink} disabled={state.busy !== null}>
                  Get a fresh link
                </button>
              </div>
            </>
          ) : (
            <Status text="Recording your visit…" />
          )}
        </div>
      );
    case "counting": {
      const ms = msUntil(a?.claimableAt, now);
      return (
        <div className="stack" style={{ gap: 8 }} data-testid="task-counting">
          <p style={{ margin: 0 }}>
            Visit recorded. You can claim in <strong role="timer" data-testid="task-countdown">{formatCountdown(ms)}</strong>.
          </p>
          <div className="row">
            <button type="button" className="btn btn-primary" disabled data-testid="task-claim">
              Claim coins ({formatCountdown(ms)})
            </button>
          </div>
          <p className="hint" style={{ margin: 0 }}>
            The time is measured by Townsy's server from your click. It is not a check that the page was read.
          </p>
        </div>
      );
    }
    case "claimable":
      return (
        <div className="stack" style={{ gap: 8 }} data-testid="task-claimable">
          <p style={{ margin: 0 }}>{kind === "partner_callback" ? `${sponsor} confirmed the outcome.` : "Welcome back."} You can claim your reward now.</p>
          <div className="row">
            <button type="button" className="btn btn-primary" onClick={() => handlers.claim()} disabled={state.busy !== null} data-testid="task-claim" data-autofocus>
              {state.busy === "claiming" ? "Claiming…" : `Claim ${coins}`}
            </button>
          </div>
        </div>
      );
    case "answering":
      return <QuizForm state={state} handlers={handlers} coins={coins} />;
    case "waiting_partner":
      return (
        <div className="stack" style={{ gap: 8 }} data-testid="task-waiting-partner">
          <Status text={`Waiting for ${sponsor} to confirm… Townsy checks every few seconds.`} />
          <p className="small" style={{ margin: 0 }}>
            You can close this window: the task keeps running until it is confirmed or expires. Continue it from Earn coins.
          </p>
          <div className="row">
            {state.outboundUrl ? (
              <SponsorLink url={state.outboundUrl} host={host} label="Open the website again" onOpened={handlers.linkOpened} />
            ) : (
              <button type="button" className="btn btn-sm" onClick={handlers.refreshLink} disabled={state.busy !== null}>
                Get a fresh link
              </button>
            )}
          </div>
        </div>
      );
    case "claimed": {
      const claim = state.claim;
      return (
        <div className="notice notice-success" role="status" data-testid="task-claimed" style={{ margin: 0 }}>
          <strong>{claim ? `${rewardText(claim.rewardCoins)} added to your coins.` : `${coins} added to your coins.`}</strong>
          {claim ? (
            <>
              {" "}
              Your coin balance: <strong data-testid="task-balance">{formatCoins(claim.coinsAvailableAfter)}</strong>.
            </>
          ) : null}
          <div className="small" style={{ marginTop: 4 }}>
            Coins buy home upgrades and items. They are not money and cannot be withdrawn or converted to Townsy Credit.
          </div>
        </div>
      );
    }
    case "expired":
    case "canceled":
      return (
        <div className="stack" style={{ gap: 8 }} data-testid={`task-${phase}`}>
          <p className="notice notice-warning small" style={{ margin: 0 }}>
            {phase === "expired"
              ? "This attempt expired before the reward was claimed. Its reservation was released; nothing was charged."
              : "You canceled this task. Its reservation was released; nothing was charged."}
          </p>
          <div className="row">
            <button type="button" className="btn btn-primary" onClick={handlers.restart} disabled={state.busy !== null} data-testid="task-restart">
              Start again
            </button>
          </div>
          <p className="hint" style={{ margin: 0 }}>
            Starting again works only if the task is still available to you (budget, today's limit).
          </p>
        </div>
      );
    case "failed":
      return (
        <p className="notice notice-warning small" style={{ margin: 0 }} data-testid="task-failed">
          {kind === "quiz"
            ? "No tries are left for this quiz. Nothing was charged, and this quiz is now closed for you."
            : `This attempt ended without a reward${a?.resolution ? ` (${a.resolution})` : ""}. Nothing was charged.`}
        </p>
      );
  }
}

function QuizForm({ state, handlers, coins }: { state: AttemptFlowState; handlers: AttemptHandlers; coins: string }) {
  const id = useId();
  const quiz = state.attempt?.quiz;
  const [choice, setChoice] = useState<number | null>(null);
  // A wrong answer clears the choice so the next try is deliberate.
  const triesLeft = quiz?.triesLeft ?? 0;
  const lastTries = useRef(triesLeft);
  useEffect(() => {
    if (triesLeft < lastTries.current) setChoice(null);
    lastTries.current = triesLeft;
  }, [triesLeft]);
  if (!quiz) return <Status text="Loading the quiz…" />;
  return (
    <form
      className="stack"
      style={{ gap: 8 }}
      data-testid="task-quiz"
      onSubmit={(e) => {
        e.preventDefault();
        if (choice !== null) handlers.claim(choice);
      }}
    >
      <p style={{ margin: 0 }} className="quiz-explanation">
        {quiz.explanation}
      </p>
      <fieldset className="choice-group quiz-choices" disabled={state.busy !== null}>
        <legend>
          <strong>{quiz.question}</strong>
        </legend>
        {quiz.choices.map((c, i) => (
          <label key={i} className="choice quiz-choice">
            <input type="radio" name={`${id}-answer`} value={i} checked={choice === i} onChange={() => setChoice(i)} data-testid={`task-choice-${i}`} />
            <span>{c}</span>
          </label>
        ))}
      </fieldset>
      <p className="small muted" style={{ margin: 0 }} data-testid="task-tries">
        {triesLeft} {triesLeft === 1 ? "try" : "tries"} left. Townsy checks the answer on its server.
      </p>
      <div className="row">
        <button type="submit" className="btn btn-primary" disabled={choice === null || state.busy !== null} data-testid="task-check-answer">
          {state.busy === "claiming" ? "Checking…" : `Check answer and claim ${coins}`}
        </button>
      </div>
    </form>
  );
}
